import { useCallback, useEffect, useId, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { Slot } from "@radix-ui/react-slot";
import { HelpTag } from "~/core/slices/help/tags";
import { useApp } from "~/core/store";
import {
    IconButton,
    Tooltip as TooltipComponent,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components";
import DiscoveryItemContent from "./DiscoveryItemContent";
import DiscoveryItemPopover from "./DiscoveryItemPopover";
import EventListener from "./EventListener";
import { ButtonRect, DiscoveryItemProps } from "./interfaces";
import useItemSetup from "./useItemSetup";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import QuestionIcon from "@material-design-icons/svg/outlined/question_mark.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const DEFAULT_MAX_WIDTH = 400;
const DEFAULT_MAX_HEIGHT = 300;

const DiscoveryItem = <T extends HelpTag>({
    tag,
    selector,
    ctx,
    top = 0,
    left = 0,
    maxWidth = DEFAULT_MAX_WIDTH,
    maxHeight = DEFAULT_MAX_HEIGHT,
    inline = false,
    flat = false,
    opened,
    onOpen,
    onClose,
    className,
    buttonClassName = "text-gray-800",
    popoverOffset,
    visibleInHistory = false,
    noOpenAction = false,
    noButton = false,
}: DiscoveryItemProps<T>) => {
    const {
        help,
        api: { discovery },
    } = useApp();
    const id = useId();
    const isOpened = useSignal(false);
    const info = discovery.getByTag(tag);
    const loaded = discovery.defer(tag, opened ?? isOpened.value);
    const loading = discovery.loading(tag);
    const defaultCoords = useSignal<ButtonRect>({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    });
    const ref = useRef<HTMLDivElement>(null);
    const isControlled = typeof opened !== "undefined";
    const helpHistory = help.history.value;
    const hasHistory = helpHistory.length > 0;
    const isInHistory = Boolean(helpHistory.find((i) => i.tag === tag));
    const isButtonOpened = isControlled ? opened : isOpened.value;
    const isActiveRecord =
        help.activeHistoryRecord.value?.tag === tag && isButtonOpened;
    const isPopoverOpened =
        !isControlled &&
        isOpened.value &&
        Boolean(loaded?.value) &&
        (isActiveRecord || !hasHistory);
    const isButtonHidden = hasHistory && !visibleInHistory;

    useItemSetup({
        ref,
        isActiveRecord,
        isInHistory,
        onClose,
    });

    useEffect(() => {
        const isDefaultOpen = help.isTagDefaultOpen(tag);

        if (isDefaultOpen) {
            isOpened.value = true;
            saveCoords();

            if (!noOpenAction) {
                window.dispatchEvent(
                    new CustomEvent("discovery.item_opened", {
                        detail: { id, tag },
                    }),
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveCoords = useCallback(() => {
        const element =
            help.history.peek().length > 0
                ? (ref.current?.closest<HTMLElement>(
                      "[data-discovery-target]",
                  ) ?? ref.current)
                : ref.current;

        if (element) {
            const { top, left, width, height } =
                element.getBoundingClientRect();

            defaultCoords.value = { top, left, width, height };
        }
    }, [defaultCoords, help]);

    const handleOpen = useCallback(() => {
        if (onOpen) {
            onOpen();
        }

        if (!isControlled) {
            isOpened.value = true;
            saveCoords();
        }

        if (!noOpenAction) {
            window.dispatchEvent(
                new CustomEvent("discovery.item_opened", {
                    detail: { id, tag },
                }),
            );
        }
    }, [tag, noOpenAction, id, isOpened, isControlled, onOpen, saveCoords]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }

        if (isOpened.peek() && !isControlled) {
            isOpened.value = false;
        }
    }, [isOpened, isControlled, onClose]);

    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();

        const v = isControlled ? opened : isOpened.peek();

        if (v) {
            handleClose();
        } else {
            handleOpen();
        }
    };

    return (
        <div
            ref={ref}
            style={{ top, left }}
            data-discovery-item={id}
            className={cn(!inline && "absolute z-50", className)}
        >
            <EventListener
                id={id}
                tag={tag}
                onOpen={handleOpen}
                onClose={handleClose}
            />
            <DiscoveryItemPopover
                tag={tag}
                id={id}
                open={isPopoverOpened}
                buttonRect={defaultCoords.value}
                ctx={ctx}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                offset={popoverOffset}
                zIndex={isInHistory ? 101 : 100}
            >
                <Slot>
                    <DiscoveryItemContent
                        tag={tag}
                        onClose={handleClose}
                        classNames={{
                            adminEdit: "-mx-4 -mb-4 mt-4 bg-gray-100 py-2 px-3",
                        }}
                    />
                </Slot>
            </DiscoveryItemPopover>
            {!noButton && (
                <TooltipProvider>
                    <TooltipComponent>
                        <TooltipTrigger disabled={isButtonHidden}>
                            <IconButton
                                onClick={handleClick}
                                flat={flat}
                                className={cn(
                                    buttonClassName,
                                    isButtonHidden && "hidden",
                                )}
                            >
                                {isButtonOpened ? (
                                    loaded?.value ? (
                                        <CloseIcon
                                            width="16"
                                            height="16"
                                            className="text-sky-500"
                                        />
                                    ) : (
                                        <Loader width="16" height="16" />
                                    )
                                ) : (
                                    <QuestionIcon width="16" height="16" />
                                )}
                            </IconButton>
                        </TooltipTrigger>
                        <TooltipContent
                            className={cn(
                                "z-[101]",
                                isPopoverOpened && "hidden",
                            )}
                            side="bottom"
                            align="center"
                        >
                            {loading?.value ? (
                                <Loader width="16" height="16" />
                            ) : (
                                (info.value?.summary?.[selector ?? ""] ??
                                info.value?.summary?.$ ??
                                "What is this?")
                            )}
                        </TooltipContent>
                    </TooltipComponent>
                </TooltipProvider>
            )}
        </div>
    );
};

const Wrapper = <T extends HelpTag>(props: DiscoveryItemProps<T>) => {
    const { help } = useApp();

    if (!help.isDiscovery.value) {
        return null;
    }

    return <DiscoveryItem {...props} />;
};

export default Wrapper;
